import React from "react";
import PropTypes from "prop-types";
import { Pagination, Table, Button, Input, Row, Col, Tooltip, Popconfirm, Divider, message, Icon } from "antd";
import { get, omit } from "lodash";
import moment from "moment";

import PageError from "App/Component/PageError";
import DialogAddArchitectAlias from "./DialogAdd";
import { Lib, Modules, requestV3 } from "App/Utils";

export default class ArchitectAliasList extends React.Component {
  constructor(props) {
    super(props)

    // --------- page config ---------
    this.page = 1
    this.limit = 100
    this.total = 0
    this.next = null

    this._savedRecords = {} // { [identifier]: record }

    this.state = {
      loading: false,
      error: null,
      aliases: [],
      searchText: "",
      visibleAdd: false,
      errors: {}, // { [identifier || pk]: { [fieldName]: errorMessage } }
    }
  }

  getEmptyRecord = () => ({
    identifier: Lib.uuid(), // Math.random().toString(),
    name: '',
  })

  getFieldError = (record, fieldName) => {
    const key = record.identifier || record.pk
    return get(this.state.errors, [key, fieldName], null)
  }

  clearFieldError = (record, fieldName) => {
    const key = record.identifier || record.pk
    if (get(this.state.errors, [key, fieldName])) {
      this.setState(prev => {
        const nextRecordErrors = { ...get(prev.errors, [key], {}) }
        delete nextRecordErrors[fieldName]
        return {
          errors: {
            ...prev.errors,
            [key]: nextRecordErrors,
          }
        }
      })
    }
  }

  renderFieldError = (record, fieldName) => {
    const errorMsg = this.getFieldError(record, fieldName)
    if (!errorMsg) return null

    return (
      <div
        className="field-error-text"
        style={{
          color: '#f5222d',
          fontSize: '11px',
          lineHeight: '1.3',
          marginTop: '4px',
          textAlign: 'left',
          wordBreak: 'break-word',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <Icon type="close-circle" theme="filled" style={{ marginRight: 4, marginTop: 2, flexShrink: 0, fontSize: 11 }} />
        <span>{errorMsg}</span>
      </div>
    )
  }

  reload = () => {
    this.page = 1
    this.readAliases()
  }

  readAliases = () => {
    this.setState({ loading: true }, () => {
      requestV3({
        urlKey: 'architectAliases-read',
        params: {
          page: this.page,
          limit: this.limit,
          architect: this.props.architectId,
        },
        useCustomErrorMessage: true,
        onSuccess: res => {
          this.next = res.next
          this.total = res.count
          this.setState({ error: null, aliases: res.results })
        },
        onFailed: error => {
          Modules.responseMessage("error", error)
          this.setState({ error: { data: error } })
        },
        onBoth: () => this.setState({ loading: false }),
      })
    })
  }

  saveAlias = ({ pk, identifier }) => {
    const recordKey = identifier || pk
    this.setState({ loading: true }, () => {
      const data = this._savedRecords[identifier];
      console.log('saveAlias', { data }, this);

      (pk ? this.updateAlias : this.createAliasNew)(pk, data)
        .then(({ id, res }) => {
          this._savedRecords[identifier] = null

          this.setState(prev => {
            const nextErrors = { ...prev.errors }
            delete nextErrors[recordKey]

            return {
              errors: nextErrors,
              aliases: prev.aliases
                .map(alias => alias.identifier === identifier ? res : alias)
            }
          })
        })
        .catch(error => {
          console.log('save alias failed', { error })

          // Parse field-level error messages dynamically
          const detail = get(error, 'detail') || get(error, 'response.data.detail') || get(error, 'data.detail') || get(error, 'response.data')
          let hasFieldErrors = false
          const fieldErrors = {}

          if (typeof detail === 'object' && detail !== null) {
            Object.keys(detail).forEach(field => {
              const errorVal = detail[field]
              if (Array.isArray(errorVal)) {
                fieldErrors[field] = errorVal.join(' ')
                hasFieldErrors = true
              } else if (typeof errorVal === 'string') {
                fieldErrors[field] = errorVal
                hasFieldErrors = true
              }
            })
          }

          if (hasFieldErrors) {
            this.setState(prev => ({
              errors: {
                ...prev.errors,
                [recordKey]: fieldErrors,
              }
            }))
          } else {
            Modules.responseMessage('error', error)
          }
        })
        .finally(() => this.setState({ loading: false }))

    })
  }

  getAllowedBody = (record) => ({
    architect: record.architect,
    name: record.name,
  })

  createAliasNew = async (_, data) => {
    return new Promise((resolve, reject) => {
      requestV3({
        method: 'post',
        urlKey: 'architectAliases-create',
        data: this.getAllowedBody({ architect: this.props.architectId, ...data }),
        onSuccess: res => resolve({ id: res.pk, res }),
        onFailed: reject,
      })
    })
  }

  updateAlias = async (id, data) => {
    return new Promise((resolve, reject) => {
      requestV3({
        method: 'put',
        urlKey: 'architectAliases-detail',
        args: [id],
        data: this.getAllowedBody({ architect: this.props.architectId, ...data }),
        onSuccess: res => resolve({ id, res }),
        onFailed: reject,
      })
    })
  }

  createSuccess = (identifier, res) => {
    this.setState({
      aliases: this.state.aliases.map(alias => alias.identifier === identifier
        ? res
        : alias),
    })
  }

  remove = (identifier) => {
    this.setState(prev => {
      const nextErrors = { ...prev.errors }
      delete nextErrors[identifier]
      return {
        errors: nextErrors,
        aliases: prev.aliases
          .filter(alias => !alias.identifier || !(alias.pk === identifier || alias.identifier === identifier)),
      }
    })
  }

  onEdit = (record) => {
    console.log("Edit clicked", record)
    const identifier = Lib.uuid()
    this._savedRecords[identifier] = record

    this.setState(prev => {
      const nextErrors = { ...prev.errors }
      delete nextErrors[record.pk]
      delete nextErrors[identifier]

      return {
        errors: nextErrors,
        aliases: prev.aliases.map(alias => alias.pk === record.pk
          ? { ...record, identifier }
          : alias
        )
      }
    })
  }

  onCancel = (record) => {
    console.log("Cancel clicked", record)
    const recordKey = record.identifier || record.pk
    this.setState(prev => {
      const nextErrors = { ...prev.errors }
      delete nextErrors[recordKey]
      return {
        errors: nextErrors,
        aliases: prev.aliases.map(alias => alias.pk === record.pk
          ? { ...record, identifier: null }
          : alias
        )
      }
    })
  }

  onDelete = (record) => {
    console.log("Delete clicked", record)
    this.setState({ loading: true }, () => {
      requestV3({
        method: 'delete',
        urlKey: 'architectAliases-delete',
        args: [record.pk],
        // urlParams: { id: record.pk },
        // onSuccess: () => this.remove(record.pk),
        onSuccess: () => this.reload(),
        onFailed: (error) => {
          console.log('onFailed', error)
          // Lib.errorMessageHandler(error)
          Modules.responseMessage("error", error)
        },
        onBoth: () => this.setState({ loading: false }),
      })
    })
  }

  handleSearch = (e) => {
    this.setState({ searchText: e.target.value });
  }

  componentDidMount() {
    this.readAliases()
  }

  render() {
    console.log('Rendered', this)
    const { aliases, loading, searchText } = this.state;

    // Filter aliases locally for search
    const filteredAliases = aliases.filter(alias => 
      alias.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns = [
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'name',
        width: 250,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text, record) => {
          if (!record.identifier)
            return <span style={{ padding: '0 8px', cursor: 'pointer' }} onClick={() => this.onEdit(record)}>{text}</span>

          const hasError = !!this.getFieldError(record, 'name')

          return (
            <div className="editable-cell-wrapper" style={{ padding: '2px 0' }}>
              <Tooltip placement="right" trigger="focus" title="Press enter to confirm">
                <Input
                  autoFocus
                  // ref={ref => this._input = ref}
                  size="small"
                  placeholder="Architect Alias"
                  defaultValue={text}
                  style={hasError ? { borderColor: '#f5222d', boxShadow: '0 0 0 2px rgba(245, 34, 45, 0.2)' } : {}}
                  onChange={e => {
                    if (!this._savedRecords[record.identifier])
                      this._savedRecords[record.identifier] = {}

                    this._savedRecords[record.identifier].name = e.target.value
                    this.clearFieldError(record, 'name')
                  }}
                  onBlur={(e) => {
                    if (!e.target.value && !record.pk)
                      this.remove(record.identifier)
                    else if (!e.target.value && record.pk)
                      this.onCancel(record)
                  }}
                  // onPressEnter={e => this.createAlias(record.identifier)}
                  onPressEnter={e => this.saveAlias(record)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      e.target.blur()

                      if (record.pk)
                        this.onCancel(record)
                    }
                  }}
                />
              </Tooltip>
              {this.renderFieldError(record, 'name')}
            </div>
          )
        }
      },
      {
        title: 'Created',
        dataIndex: 'created',
        key: 'created',
        width: 120,
        render: (text) => text ? moment(text).format('DD MMM YYYY, HH:mm') : '-',
        // sorter: (a, b) => moment(a.created).unix() - moment(b.created).unix(),
      },
      {
        title: 'Modified',
        dataIndex: 'modified',
        key: 'modified',
        width: 120,
        render: (text) => text ? moment(text).format('DD MMM YYYY, HH:mm') : '-',
        // sorter: (a, b) => moment(a.modified).unix() - moment(b.modified).unix(),
      },
      {
        title: 'Operation',
        key: 'operation',
        width: 120,
        align: 'center',
        render: (text, record) => !record.identifier ? (
          <span>
            {/* <Tooltip title="Edit" mouseLeaveDelay={0}> */}
              <Button type="primary" size="small" icon="edit" onClick={() => this.onEdit(record)} ghost />
            {/* </Tooltip> */}
            <Divider type="vertical" />
            <Popconfirm title="Are you sure delete this alias?" onConfirm={() => this.onDelete(record)}>
              {/* <Tooltip title="Delete" mouseLeaveDelay={0}> */}
                <Button type="danger" size="small" icon="delete" ghost />
              {/* </Tooltip> */}
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Button type="primary" size="small" icon="save" onClick={() => this.saveAlias(record)} />
            <Divider type="vertical" />
            <Button
              // type="danger"
              size="small"
              icon="close"
              // onClick={() => this.remove(record.identifier)}
              onClick={() => {
                const recordKey = record.identifier || record.pk
                if (record.pk)
                  this.setState(prev => {
                    const nextErrors = { ...prev.errors }
                    delete nextErrors[recordKey]
                    return {
                      errors: nextErrors,
                      aliases: prev.aliases.map(alias => alias.identifier === record.identifier
                        ? omit(alias, ['identifier'])
                        : alias
                      )
                    }
                  })
                else
                  this.setState(prev => {
                    const nextErrors = { ...prev.errors }
                    delete nextErrors[recordKey]
                    return {
                      errors: nextErrors,
                      aliases: prev.aliases.filter(alias => alias.identifier !== record.identifier )
                    }
                  })
              }}
            />
          </span>
        ),
      },
    ];

    if (this.state.error && !this.state.loading) {
      return (
        <PageError
          errorResponse={this.state.error}
          onGoBack={() => this.props.history.goBack()}
          onReload={this.reload}
        />
      )
    }

    return (
      <div>
        <DialogAddArchitectAlias
          visible={this.state.visibleAdd}
          onClose={() => this.setState({ visibleAdd: false })}
        />

        <Row type="flex" justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col>
            <Button
              type="primary"
              icon="plus"
              onClick={() => {
                this.setState({
                  aliases: [this.getEmptyRecord()]
                    .concat(this.state.aliases),
                })
              }}
            >
              Add
            </Button>
          </Col>
          <Col>
            <Row type="flex" gutter={[12, 12]}>
              <Col>
                <Button icon="reload" onClick={this.reload} loading={loading}>
                  Reload
                </Button>
              </Col>
              {/* <Col>
                <Input.Search
                  placeholder="Search aliases..."
                  value={searchText}
                  onChange={this.handleSearch}
                  style={{ width: 250 }}
                />
              </Col> */}
            </Row>
          </Col>
        </Row>
        
        <Table
          rowKey={(record) => record.pk || record.identifier}
          columns={columns}
          dataSource={filteredAliases}
          loading={loading}
          // pagination={{
          //   // pageSize: this.limit,
          //   total: this.total,
          // }}
          pagination={false}
          bordered
          size="small"
          className="styled-table-small"
        />
        <Row className="mt-3" type="flex" justify="end">
          <Pagination
            current={this.page}
            total={this.total}
            pageSize={this.limit}
            onChange={(page, pageSize) => {
              this.page = page
              this.limit = pageSize
              this.readAliases()
            }}
          />
        </Row>
      </div>
    )
  }
}

ArchitectAliasList.propTypes = {
  architectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

ArchitectAliasList.defaultProps = {
}